import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/use-toast";
import { Loader2, X, Upload } from "lucide-react";
import { Card } from "./ui/card";

export const AboutManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const { data: aboutContent, isLoading } = useQuery({
    queryKey: ["about-content"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_about_content")
        .select(`
          *,
          images:sns_about_images(*)
        `)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const updateContent = useMutation({
    mutationFn: async (values: {
      title: string;
      description: string;
      video_url?: string;
    }) => {
      const { error } = await supabase
        .from("sns_about_content")
        .upsert({ ...values, id: aboutContent?.id || 1 });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["about-content"] });
      toast({
        title: "Success",
        description: "About content updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update content: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const files = event.target.files;
      if (!files || !aboutContent?.id) return;

      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split(".").pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("about-images")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("about-images")
          .getPublicUrl(fileName);

        return supabase
          .from("sns_about_images")
          .insert({
            about_id: aboutContent.id,
            image_url: publicUrl,
            caption: file.name
          });
      });

      await Promise.all(uploadPromises);

      queryClient.invalidateQueries({ queryKey: ["about-content"] });
      toast({
        title: "Success",
        description: "Images uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to upload images: " + error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExt = file.name.split(".").pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("about-images")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("about-images")
        .getPublicUrl(fileName);

      updateContent.mutate({
        ...aboutContent,
        video_url: publicUrl,
      });

      toast({
        title: "Success",
        description: "Video uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to upload video: " + error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    try {
      const { error } = await supabase
        .from("sns_about_images")
        .delete()
        .eq("id", imageId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["about-content"] });
      toast({
        title: "Success",
        description: "Image deleted successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete image: " + error.message,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const title = formData.get("title") as string;
    const description = formData.get("description") as string;
    const video_url = formData.get("video_url") as string;

    updateContent.mutate({
      title,
      description,
      video_url,
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-bold mb-6">Manage About Section</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Title</label>
          <Input
            name="title"
            defaultValue={aboutContent?.title}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Description (one paragraph per line)
          </label>
          <Textarea
            name="description"
            defaultValue={aboutContent?.description || ""}
            rows={6}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Video URL (optional)</label>
          <Input
            name="video_url"
            type="url"
            defaultValue={aboutContent?.video_url || ""}
            placeholder="Enter video URL"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Images</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {aboutContent?.images?.map((image: any) => (
              <Card key={image.id} className="relative group">
                <img
                  src={image.image_url}
                  alt={image.caption}
                  className="w-full h-48 object-cover rounded-t"
                />
                <div className="p-2">
                  <p className="text-sm text-gray-600 truncate">{image.caption}</p>
                </div>
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDeleteImage(image.id)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </Card>
            ))}
          </div>
          <Input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            disabled={uploading}
          />
          <p className="text-sm text-gray-500 mt-1">
            You can select multiple images at once
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Video File (optional)</label>
          {aboutContent?.video_url && (
            <video
              src={aboutContent.video_url}
              controls
              className="w-64 h-auto rounded mb-4"
            >
              Your browser does not support the video tag.
            </video>
          )}
          <Input
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            disabled={uploading}
          />
        </div>

        <Button type="submit" disabled={updateContent.isPending || uploading}>
          {(updateContent.isPending || uploading) ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : null}
          Save Changes
        </Button>
      </form>
    </div>
  );
};